import React, { useState } from "react";
import { listHypotheses, createHypothesis, updateHypothesis, deleteHypothesis } from "../api/services";
import { useData } from "../hooks/useData";
import { Card, Loading, Button } from "../components/common/UI";
import { IconPlus, IconTrash } from "../components/common/Icons";
import Modal from "../components/common/Modal";
import styles from "./pages.module.css";

// Три ключевых продукта + гипотезы на всю экосистему сразу
const PRODUCTS = [
  { key: "", label: "— не выбран —" },
  { key: "rublox", label: "Рублокс" },
  { key: "minecraftia", label: "Майнкрафтия" },
  { key: "maska", label: "Маська" },
  { key: "ecosystem", label: "Вся экосистема" },
];
const PRODUCT_LABELS = Object.fromEntries(PRODUCTS.map((p) => [p.key, p.label]));

// Статус гипотезы = положение её карточки в канбане
const STATUS_LABELS = {
  backlog: { t: "бэклог", c: "var(--text-mute)" },
  planned: { t: "на неделю", c: "var(--accent)" },
  in_progress: { t: "в работе", c: "#d29922" },
  measuring: { t: "замеряем", c: "#39c5cf" },
  done: { t: "готово", c: "#3fb950" },
  archived: { t: "архив", c: "var(--text-mute)" },
};

// ICE-приоритизация: каждый критерий 1–10, скор = среднее
const ICE_FIELDS = [
  ["ice_impact", "Влияние", "насколько сильно продвинет к цели 1 млн ₽/мес"],
  ["ice_confidence", "Уверенность", "насколько верим, что гипотеза сработает"],
  ["ice_ease", "Простота", "насколько быстро и дёшево проверить (10 — за день)"],
];
const emptyForm = {
  title: "", statement: "", success_metric: "", guardrail_metric: "",
  expected_result: "", how_to_measure: "", resources: "",
  ice_impact: 5, ice_confidence: 5, ice_ease: 5, product: "",
};

export default function Hypotheses() {
  const { data, loading, reload } = useData(() => listHypotheses(), []);
  const [modal, setModal] = useState(null); // null | {form, id}

  const openNew = () => setModal({ form: { ...emptyForm }, id: null });
  const openEdit = (h) => setModal({ form: { ...emptyForm, ...h }, id: h.id });

  const save = async () => {
    const { form, id } = modal;
    if (!form.title.trim()) return;
    if (id) await updateHypothesis(id, form);
    else await createHypothesis(form);
    setModal(null);
    reload();
  };

  const remove = async (id) => {
    await deleteHypothesis(id);
    reload();
  };

  if (loading || !data) return <Loading />;
  const items = data.hypotheses || [];

  return (
    <div className={styles.page}>
      <div className={styles.pageHead}>
        <div>
          <h1 className={styles.pageTitle}>Гипотезы</h1>
          <div className={styles.pageSub}>Бэклог с приоритизацией по ICE (Влияние · Уверенность · Простота); лучшие уходят в канбан недели</div>
        </div>
        <Button onClick={openNew}><IconPlus width={16} height={16} /> Новая гипотеза</Button>
      </div>

      <Card>
        {items.length === 0 ? (
          <div style={{ color: "var(--text-mute)", padding: 20 }}>Пока нет гипотез. Добавь первую.</div>
        ) : (
          <table className={styles.table} style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--text-dim)", fontSize: 12.5 }}>
                <th style={{ padding: "8px 6px" }}>Гипотеза</th>
                <th>Метрика успеха</th>
                <th>Продукт</th>
                <th>Статус</th>
                <th style={{ textAlign: "center" }}>ICE</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((h) => (
                <tr key={h.id} style={{ borderTop: "1px solid var(--border)", cursor: "pointer" }}
                    onClick={() => openEdit(h)}>
                  <td style={{ padding: "10px 6px", fontWeight: 500 }}>{h.title}</td>
                  <td style={{ color: "var(--text-dim)" }}>{h.success_metric || "—"}</td>
                  <td style={{ color: "var(--text-dim)" }}>{PRODUCT_LABELS[h.product] || h.product || "—"}</td>
                  <td>
                    <span style={{ color: (STATUS_LABELS[h.status] || {}).c || "var(--text-dim)", fontSize: 12.5 }}>
                      {(STATUS_LABELS[h.status] || {}).t || h.status}
                    </span>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <span style={{ background: "var(--accent-soft)", color: "var(--accent)", padding: "2px 9px", borderRadius: 20, fontWeight: 600 }}>
                      {h.ice_score}
                    </span>
                  </td>
                  <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                    <button className={styles.iconBtn} onClick={() => remove(h.id)}
                            style={{ background: "none", border: "none", color: "var(--text-mute)", padding: 6 }}>
                      <IconTrash width={16} height={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {modal && (
        <HypothesisModal
          state={modal}
          setState={setModal}
          onSave={save}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

function HypothesisModal({ state, setState, onSave, onClose }) {
  const { form } = state;
  const set = (k, v) => setState({ ...state, form: { ...form, [k]: v } });
  const ice = ((Number(form.ice_impact) + Number(form.ice_confidence) + Number(form.ice_ease)) / 3).toFixed(2);

  return (
    <Modal
      title={state.id ? "Гипотеза" : "Новая гипотеза"}
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button onClick={onSave}>Сохранить</Button>
        </>
      }
    >
      <div className={styles.field}>
        <label>Название</label>
        <input value={form.title} onChange={(e) => set("title", e.target.value)} autoFocus />
      </div>
      <div className={styles.field}>
        <label>Формулировка (Если … то … потому что …)</label>
        <textarea rows={2} value={form.statement} onChange={(e) => set("statement", e.target.value)} />
      </div>
      <div className={styles.formRow}>
        <div className={styles.field}>
          <label>Метрика успеха</label>
          <input value={form.success_metric} onChange={(e) => set("success_metric", e.target.value)} />
        </div>
        <div className={styles.field}>
          <label>Guardrail (что не должно просесть)</label>
          <input value={form.guardrail_metric} onChange={(e) => set("guardrail_metric", e.target.value)} />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.field}>
          <label>Ожидаемый результат</label>
          <input value={form.expected_result} onChange={(e) => set("expected_result", e.target.value)} />
        </div>
        <div className={styles.field}>
          <label>Как измеряем</label>
          <input value={form.how_to_measure} onChange={(e) => set("how_to_measure", e.target.value)} />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.field}>
          <label>Ресурсы</label>
          <input value={form.resources} onChange={(e) => set("resources", e.target.value)} />
        </div>
        <div className={styles.field}>
          <label>Продукт</label>
          <select value={form.product || ""} onChange={(e) => set("product", e.target.value)}>
            {PRODUCTS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
      </div>
      <div className={styles.iceRow}>
        {ICE_FIELDS.map(([k, label, hint]) => (
          <div className={styles.field} key={k}>
            <label>{label}: {form[k]}</label>
            <input type="range" min="1" max="10" value={form[k]}
                   onChange={(e) => set(k, Number(e.target.value))} />
            <div style={{ color: "var(--text-mute)", fontSize: 11.5, lineHeight: 1.4, marginTop: 4 }}>
              {hint}
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8, color: "var(--text-dim)" }}>
        ICE-скор: <b style={{ color: "var(--accent)" }}>{ice}</b>
        <span style={{ color: "var(--text-mute)", fontSize: 12 }}> — среднее трёх оценок; чем выше, тем раньше берём в работу</span>
      </div>
    </Modal>
  );
}
